<template>
    <v-form class="cal-form" ref="form" :onSuccess="newSubmit">
        <div class="cal-form-item">
            <label>{{
                info.homePriceText || $t('md-calculator.homePrice')
            }}</label>
            <v-input
                class="input left-padding"
                :value="payment.total"
                type="dollar"
                name="total"
                :required="true"
                :validateConfig="homePriceConfig"
                @input="onTotalChange"
            >
                <span class="prefix">{{ currencySymbol }}</span>
            </v-input>
        </div>
        <div class="cal-form-item">
            <label>
                {{ info.downPaymentText || $t('md-calculator.downPayment') }}
                <span
                    @click="openWarn"
                    class="iconfont icon-warn"
                    v-outsideclick="hideWarn"
                    v-if="ratio < 20"
                >
                    <div class="pop-warn" v-show="showWarn">
                        {{
                            info.downPaymentWarnText ||
                                $t('md-calculator.ifYourDownPayment')
                        }}
                    </div>
                </span>
            </label>
            <div class="input-container">
                <v-input
                    class="input right-padding left-padding left-radius no-border-right"
                    style="flex: 1.8 1 0"
                    :value="payment.downPaymentValue"
                    type="dollar"
                    :required="true"
                    name="downPaymentValue"
                    :validateConfig="patternConfig"
                    @input="onDownpaymentValueChange"
                >
                    <span class="prefix">{{ currencySymbol }}</span>
                </v-input>
                <v-input
                    style="width: 90px"
                    class="input right-padding right-radius"
                    :value="payment.downPaymentRatio"
                    type="number"
                    :required="true"
                    name="downPaymentRatio"
                    :validateConfig="{ min: 0, max: 100 }"
                    @change="onDownpaymentRatioChange"
                >
                    <span name="footer" class="surfix">%</span>
                </v-input>
            </div>
        </div>
        <div class="cal-form-item">
            <label>{{
                info.loanTermText || $t('md-calculator.loanTerm')
            }}</label>
            <select
                class="input"
                :value="payment.loanTerm"
                type="select"
                name="loanTerm"
                @change="changeLoanTerm"
            >
                <option
                    :value="option.value"
                    :key="option.value"
                    v-for="option in loanTermOptions"
                >
                    {{ option.label }}
                </option>
            </select>
        </div>
        <div class="cal-form-item">
            <label>{{
                info.interestRateText || $t('md-calculator.interestRate')
            }}</label>
            <v-input
                class="input right-padding"
                :value="payment.interestRate"
                type="number"
                name="interestRate"
                :required="true"
                :validateConfig="{ min: 0, max: 50 }"
                @change="onInputChange"
            >
                <span name="footer" class="surfix">%</span>
            </v-input>
        </div>
        <div class="cal-form-item">
            <label>{{
                info.propertyTaxRateText || $t('md-calculator.propertyTax')
            }}</label>
            <div class="input-container">
                <v-input
                    class="input right-padding left-padding right-year-padding left-radius no-border-right"
                    :value="payment.propertyTax"
                    type="dollar"
                    style="flex: 1.8 1 0"
                    name="propertyTax"
                    :validateConfig="patternConfig"
                    @change="onPropertyTaxChange"
                >
                    <span class="prefix">{{ currencySymbol }}</span>
                    <span name="footer" class="surfix">{{
                        $t('md-calculator.year')
                    }}</span>
                </v-input>
                <v-input
                    class="input right-padding right-radius"
                    :value="payment.propertyTaxRate"
                    style="width: 80px"
                    type="number"
                    name="propertyTaxRate"
                    :validateConfig="{ min: 0, max: 50 }"
                    @change="onPropertyTaxChange"
                >
                    <span name="footer" class="surfix">%</span>
                </v-input>
            </div>
        </div>
        <div class="cal-form-item" v-show="showMore">
            <label>{{
                info.homeInsuranceText || $t('md-calculator.homeInsurance')
            }}</label>
            <v-input
                class="input left-padding right-lg-padding"
                :value="payment.homeInsurance"
                type="dollar"
                name="homeInsurance"
                :validateConfig="patternConfig"
                @input="onInputChange"
            >
                <span class="prefix">{{ currencySymbol }}</span>
                <span name="footer" class="surfix">{{
                    $t('md-calculator.year')
                }}</span>
            </v-input>
        </div>
        <div class="cal-form-item" v-show="showMore">
            <label>{{
                info.condoFeesText || $t('md-calculator.condohoaFees')
            }}</label>
            <v-input
                class="input left-padding right-lg-padding"
                :value="payment.hoaFee"
                type="dollar"
                name="hoaFee"
                :validateConfig="patternConfig"
                @change="onInputChange"
            >
                <span class="prefix">{{ currencySymbol }}</span>
                <span name="footer" class="surfix">{{
                    $t('md-calculator.month')
                }}</span>
            </v-input>
        </div>
        <div class="submit-container">
            <span class="more" @click="toggle()">
                {{
                    showMore
                        ? simpleText || $t('md-calculator.simple')
                        : advancedText || $t('md-calculator.advanced')
                }}
                <i
                    class="iconfont icon-arrow-down"
                    :class="{ up: showMore }"
                ></i>
            </span>
            <button
                type="submit"
                class="submit"
                :aria-label="$t('md-calculator.submitTheFormForCalculation')"
            >
                {{ $t('md-calculator.calculate') }}
            </button>
        </div>
    </v-form>
</template>

<script>
import { mapState as rootMapState } from 'pinia';
import { useUserStore } from '@/store';
import calFormula from '../../../components/caculator/lib/calFormula';
import vForm from '../../../components/v-form.vue';
import vInput from '../../../components/v-input.vue';
import signlog from '../../../components/sign-log';
import calculator from '@/components/caculator/calculator.js';

export default {
    emits: ['submit', 'changePayment'],
    i18next: {
        ns: 'md-calculator'
    },
    mixins: [calculator],
    components: { vForm, vInput },
    props: {
        payment: {
            type: Object,
            required: true
        },
        needRegister: {
            type: Boolean,
            required: true
        },
        info: {
            type: Object,
            default: null
        },
        simpleText: {
            type: String,
            default: null
        },
        advancedText: {
            type: String,
            default: null
        }
    },
    computed: {
        ...rootMapState(useUserStore, {
            "user": store => store.user
        }),
        currencySymbol() {
            return '$';
        },
        
        patternConfig() {
            return {
                pattern: /^[,\d]+(\.[,\d]+)?$/,
                'pattern-msg': this.$t('md-calculator.pleaseEnterAValid')
            };
        },
        homePriceConfig() {
            return {
                pattern: /^[0-9,.]{5,12}$/,
                'pattern-msg': this.$t('md-calculator.pleaseEnterAValid')
            };
        },
        loanTermOptions() {
            return (
                this.info.loanTermOptions || [
                    {
                        value: 10,
                        label: this.$t('md-calculator.yearFixed', { year: 10 })
                    },
                    {
                        value: 15,
                        label: this.$t('md-calculator.yearFixed', { year: 15 })
                    },
                    {
                        value: 20,
                        label: this.$t('md-calculator.yearFixed', { year: 20 })
                    },
                    {
                        value: 25,
                        label: this.$t('md-calculator.yearFixed', { year: 25 })
                    },
                    {
                        value: 30,
                        label: this.$t('md-calculator.yearFixed', { year: 30 })
                    },
                    {
                        value: 40,
                        label: this.$t('md-calculator.yearFixed', { year: 40 })
                    }
                ]
            );
        }
    },
    data: function() {
        return {
            showMore: false,
            cacheData: null,
            showWarn: false,
            ratio: 0
        };
    },
    created() {
        this.setInputValue('downPaymentValue', (this.payment.total * this.payment.downPaymentRatio) / 100)
        this.ratio = +this.payment.downPaymentRatio;
    },
    mounted() {
        this.doCaculate(Object.assign({}, this.payment), true);
    },
    methods: {
        changeLoanTerm(event) {
            console.log(event);
            this.setInputValue('loanTerm', event.target.value)
        },
        toggle() {
            this.showMore = !this.showMore;
        },
        refresh() {
            this.doCaculate(this.cacheData);
        },
        newSubmit(data) {
            const callback = () => {
                this.submit(data);
            };
            this.submitAndPhoneValid(callback);
        },
        submit(data) {
            delete data['undefined'];
            Object.keys(data).forEach(key => {
                data[key] = ('' + data[key]).replace(/,/g, '');
            });

            let diff = false;
            for (let key in data) {
                if (+this.cacheData[key] != +data[key]) {
                    diff = true;
                    break;
                }
            }
            if (diff) {
                this.sendTimeline(data);
            }
            this.doCaculate(data);
        },
        sendTimeline({ total, downPaymentRatio, loanTerm }) {
            this.axiosIns
                .get(
                    `/api-site/mortgage/calculator-tracking?homePrice=${total}&downPayment=${downPaymentRatio}&loanTerm=${loanTerm}&calculatorType=mortgage`
                )
                .catch(() => {});
        },
        doCaculate(data, inital) {
            if (!inital && !this.user && this.needRegister) {
                signlog.register({
                    onregister: () => {
                        this.doCaculate(data, inital);
                    },
                    onLogin: () => {
                        this.doCaculate(data, inital);
                    }
                });
                return;
            }
            this.cacheData = Object.assign({}, data);
            let result = calFormula(data);
            this.$emit('submit', result, data);
        },
        onTotalChange(name, value) {
            value = ('' + value).replace(/,/g, '');
            if (!isNaN(value)) {
                value = (+value).toFixed(0);
                this.setInputValue('total', value);
                this.setInputValue(
                    'downPaymentValue',
                    ((value * this.payment.downPaymentRatio) / 100).toFixed(0)
                );
                this.setInputValue(
                    'propertyTax',
                    ((value * this.payment.propertyTaxRate) / 100).toFixed(0)
                )
            } else {
                this.setInputValue('total', 0);
                this.setInputValue('downPaymentValue', 0);
                this.setInputValue(
                    'propertyTax',
                    0
                )
            }
        },
        onDownpaymentValueChange(name, value) {
            value = ('' + value).replace(/,/g, '');
            if (!isNaN(value)) {
                value = (+value).toFixed(0);
                this.setInputValue('downPaymentValue', value);
                if (+this.payment.total != 0) {
                    let ratio = ((value / this.payment.total) * 100).toFixed(3);
                    this.setInputValue('downPaymentRatio', ratio);
                    this.ratio = +ratio;
                } else {
                    this.setInputValue('downPaymentRatio', '0.000');
                    this.ratio = 0;
                }
            } else {
                this.setInputValue('downPaymentValue', 0);
            }
        },
        onDownpaymentRatioChange(name, value) {
            if (!isNaN(value)) {
                value = (+value).toFixed(3);
                this.setInputValue('downPaymentRatio', value);
                this.ratio = +value;
                this.setInputValue(
                    'downPaymentValue',
                    ((this.payment.total * value) / 100).toFixed(0)
                );
            }
        },
        onPropertyTaxChange(name, value) {
            value = ('' + value).replace(/,/g, '');
            if (!isNaN(value)) {
                let v = (+value).toFixed(3);
                this.setInputValue(name, v);
            } else {
                value = 0;
                this.setInputValue(name, 0);
            }
            let total = this.payment.total || 0;
            const propertyValueRel = {
                propertyTax: 'propertyTaxRate',
                propertyTaxRate: 'propertyTax'
            };
            if (name === 'propertyTax') {
                let rate = ((+value / +total) * 100).toFixed(3);
                this.setInputValue(propertyValueRel[name], rate);
            } else {
                let amount = ((total * +value) / 100).toFixed(0);
                this.setInputValue(propertyValueRel[name], amount);
            }
        },
        onInputChange(name, value) {
            value = ('' + value).replace(/,/g, '');
            if (!isNaN(value)) {
                let v = (+value).toFixed(3);
                this.setInputValue(name, v);
            } else {
                this.setInputValue(name, 0);
            }
        },
        setInputValue(name, value) {
            this.$refs.form?.vInputsMap?.[name] &&
                (this.$refs.form.vInputsMap[name].inputValue = value);
            this.$emit('changePayment', { key: name, value })
        },
        openWarn() {
            this.showWarn = true;
        },
        hideWarn() {
            this.showWarn = false;
        }
    }
};
</script>
<style lang="scss">
@import '../../../theme/media.scss';
@import '@/theme/mixin.scss';
.md-calculator.broker {
    .cal-form {
        .cal-form-item {
            margin-bottom: 14px;
            label {
                font-size: 14px;
                color: #505050;
                margin-bottom: 6px;
                display: block;
                .icon-warn {
                    color: #5a5f6c;
                    margin-left: 10px;
                    position: relative;
                    .pop-warn {
                        position: absolute;
                        top: 50%;
                        right: -10px;
                        transform: translateY(-50%) translateX(100%);
                        background: #2a2a2a;
                        opacity: 0.9;
                        font-size: 13px;
                        box-shadow: 0 1px 2px 0 rgba(116, 71, 71, 0.3);
                        width: 360px;
                        z-index: var(--level-low);
                        padding: 14px;
                        color: white;
                        border-radius: 1px;
                        &:before {
                            content: '';
                            display: block;
                            border-top: 10px solid transparent;
                            border-bottom: 10px solid transparent;
                            border-right: 10px solid #2a2a2a;
                            position: absolute;
                            left: 0px;
                            top: 50%;
                            transform: translate(-100%, -50%);
                        }
                    }
                }
            }
            .input-container {
                display: flex;
            }
            select {
                height: 40px;
                border-radius: 5px;
                padding-left: 10px;
                padding-right: 0;
                width: 100%;
                box-shadow: none;
                border: 1px solid #dadada;
                font-size: 14px;
                font-family: var(--font-bold);
                color: #191919;
                background: #fff
                    url('//cdn.chime.me/image/fs01/test-image-upload/20170226/19/original_12078234279803677.png')
                    96% no-repeat;
                background-size: 9px 5px;
                @include media-600 {
                    background: #fff
                        url('//cdn.chime.me/image/fs01/test-image-upload/20170226/19/original_12078234279803677.png')
                        97% no-repeat;
                    background-size: 9px 5px;
                }
            }
            .input {
                position: relative;
                .prefix,
                .surfix {
                    position: absolute;
                    top: 50%;
                    z-index: 1;
                    transform: translate(0, -50%);
                }
                .prefix {
                    left: 10px;
                    color: #a8a8a8;
                }
                .surfix {
                    right: 10px;
                    color: #a8a8a8;
                }
                input {
                    font-size: 14px;
                    font-family: var(--font-bold);
                    height: 40px;
                    line-height: 38px;
                    border-radius: 5px;
                    padding-left: 10px;
                    padding-right: 10px;
                    width: 100%;
                    box-shadow: none;
                    border: 1px solid #dadada;
                    color: #191919;
                }
                &.right-year-padding input {
                    padding-right: 47px !important;
                }
                &.left-padding {
                    input {
                        padding-left: 20px;
                    }
                    &.cad-symbol {
                        input {
                            padding-left: 30px;
                        }
                    }
                }
                &.right-padding {
                    input {
                        padding-right: 22px;
                    }
                }
                &.right-lg-padding {
                    input {
                        padding-right: 70px;
                    }
                }
                &.left-radius {
                    input {
                        border-radius: 5px 0 0 5px;
                    }
                }
                &.right-radius {
                    input {
                        border-radius: 0 5px 5px 0;
                    }
                }
                &.no-border-right {
                    input {
                        border-right: none;
                    }
                }
            }
        }
        .submit-container {
            margin-top: 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            .more {
                font-size: 14px;
                color: var(--color-text);
                cursor: pointer;
                &:hover {
                    @include color-theme();
                }
                .iconfont {
                    font-size: 12px;
                    display: inline-block;
                    &.up {
                        transform: rotate(180deg);
                    }
                }
            }
            .submit {
                @include color-button-hover();
                height: 40px;
                width: 123px;
                border-radius: 20px;
                font-size: 14px;
                @include color-button();
            }
        }
    }
}
@include media-600 {
    .md-calculator.broker {
        .cal-form {
            width: 100%;
            .submit-container {
                padding-top: 5px;
            }
        }
    }
}
</style>
